import { Theme } from "@material-ui/core";
import CheckCircle from "@material-ui/icons/CheckCircle";
import RadioButtonUnchecked from "@material-ui/icons/RadioButtonUnchecked";
import RemoveCircle from "@material-ui/icons/RemoveCircle";

export const createThemeComponents = (theme: Theme) => ({
  MuiAccordion: {
    styleOverrides: {
      root: {
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(2),
        "&.Mui-expanded:last-of-type": {
          marginBottom: theme.spacing(2),
        },
        "&:before": {
          content: "none",
        },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        color: "#000000",
      },
    },
  },
  MuiAccordionDetails: {
    styleOverrides: {
      root: {
        padding: theme.spacing(1, 2, 2),
      },
    },
  },
  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        padding: theme.spacing(1.5, 2),
        minHeight: 44,
      },
      content: {
        margin: 0,
      },
    },
  },
  MuiAppBar: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        "&.MuiAppBar-colorDefault": {
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
        },
      },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: {
        color: "inherit",
        backgroundColor: theme.palette.background.default,
      },
    },
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
      size: "small" as "small",
    },
    styleOverrides: {
      root: {
        padding: "6px 14px",
        minHeight: 32,
        fontSize: "0.8125rem",
        textTransform: "none" as any,
      },
      label: {
        fontWeight: theme.typography.fontWeightMedium,
      },
      text: {
        padding: "6px 10px",
      },
      sizeLarge: {
        padding: "8px 20px",
        minHeight: 40,
        fontSize: "0.875rem",
      },
    },
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true, // No more ripple, on the whole application
    },
  },
  /** Mesmo visual dos cards do dashboard (borda, sombra suave, sem elevation Material). */
  MuiCard: {
    defaultProps: {
      elevation: 0,
      variant: "elevation" as "elevation",
    },
    styleOverrides: {
      root: {
        borderRadius: 8,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: "#ffffff",
        backgroundImage: "none",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06), 0 4px 16px rgba(15, 23, 42, 0.04)",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        "&:hover": {
          boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08), 0 8px 24px rgba(39, 178, 121, 0.08)",
        },
      },
    },
  },
  MuiCardActions: {
    styleOverrides: {
      root: {
        justifyContent: "flex-end",
        padding: "0 16px 16px 16px",
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: theme.spacing(2),
        "&:last-child": {
          paddingBottom: theme.spacing(2),
        },
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: {
        padding: "16px 16px 0 16px",
      },
    },
  },
  MuiCheckbox: {
    defaultProps: {
      checkedIcon: <CheckCircle />,
      indeterminateIcon: <RemoveCircle />,
      icon: <RadioButtonUnchecked />,
    },
  },
  MuiChip: {
    defaultProps: {
      size: "small" as "small",
    },
    styleOverrides: {
      root: {
        height: 26,
        fontSize: "0.75rem",
      },
      label: {
        fontWeight: theme.typography.fontWeightMedium,
        paddingLeft: 8,
        paddingRight: 8,
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: 12,
        gap: 8,
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: "16px 16px 8px 16px",
        "& .MuiTypography-root": {
          fontSize: "1.1rem",
        },
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: "8px 16px 16px 16px",
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        border: "none; !important",
      },
    },
  },
  MuiFab: {
    styleOverrides: {
      root: {
        boxShadow: "none",
        lineHeight: "inherit",
        textTransform: "none" as any,
        "&.MuiFab-secondary": {
          color: theme.palette.text.primary,
        },
      },
    },
  },
  MuiFilledInput: {
    defaultProps: {
      disableUnderline: true,
    },
    styleOverrides: {
      root: {
        borderRadius: theme.shape.borderRadius,
      },
    },
  },
  MuiInternalClock: {
    styleOverrides: {
      clock: {
        backgroundColor: theme.palette.background.default,
      },
    },
  },
  MuiInternalDateTimePickerTabs: {
    styleOverrides: {
      tabs: {
        backgroundColor: theme.palette.background.default,
        "& MuiTabs-indicator": {
          height: 0,
        },
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        height: 6,
      },
    },
  },
  MuiList: {
    defaultProps: {
      disablePadding: true,
    },
  },
  MuiListItem: {
    defaultProps: {
      dense: true,
    },
    styleOverrides: {
      root: {
        borderRadius: 12,
        paddingTop: 6,
        paddingBottom: 6,
        minHeight: 40,
        "&.Mui-selected-conf": {
          color: theme.palette.text.primary,
          fontWeight: 900,
        },
        "&.Mui-selected": {
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
        },
      },
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        minWidth: 36,
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      list: {
        paddingRight: 8,
        paddingLeft: 8,
      },
    },
  },
  MuiMenuItem: {
    defaultProps: {
      dense: true,
    },
    styleOverrides: {
      root: {
        paddingTop: 6,
        paddingBottom: 6,
        minHeight: 36,
        fontSize: "0.8125rem",
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: {
        /* padding: "6px 6px",
        fontSize: "0.8rem", */
        "&:-webkit-autofill": {
          WebkitBoxShadow: `0 0 0 30px ${theme.palette.background.paper} inset`,
        },
      },
    },
  },
  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        backgroundImage: "none",
      },
    },
  },
  MuiRadio: {
    defaultProps: {
      color: "primary" as "primary",
    },
  },
  MuiTabs: {
    styleOverrides: {
      flexContainer: {
        alignItems: "center",
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        borderRadius: "50rem",
        padding: "8px 12px",
        minHeight: 44,
        fontSize: "0.8125rem",
        maxWidth: "initial !important",
        minWidth: "initial !important",
        textTransform: "none" as any,
        /* ButtonBase usa alignItems: center; stretch deixa o wrapper ocupar a altura e centralizar ícone+label */
        alignItems: "stretch",
        "&.Mui-selected": {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
      },
      wrapper: {
        flex: 1,
        justifyContent: "center",
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: `1px solid ${theme.palette.divider}`,
      
        fontSize: "0.8125rem",
      },
      sizeSmall: {
        padding: "6px 10px",
        fontSize: "0.75rem",
      },
    },
  },
  MuiTimeline: {
    styleOverrides: {
      root: {
        padding: "0 0 0 16px",
      },
    },
  },
  MuiTimelineContent: {
    styleOverrides: {
      root: {
        padding: "12px 16px",
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        color: theme.palette.text.secondary,
        borderRadius: "12px !important",
        border: "none",
        textTransform: "none" as any,
        "&.Mui-selected": {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
      },
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: {
        backgroundColor: theme.palette.background.default,
        padding: 3,
      },
    },
  },
  MuiIconButton: {
    defaultProps: {
      size: "small" as "small",
    },
    styleOverrides: {
      root: {
        padding: 6,
      },
      sizeSmall: {
        padding: 4,
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      size: "small" as "small",
      margin: "dense" as "dense",
    },
  },
  MuiFormControl: {
    defaultProps: {
      margin: "dense" as "dense",
      size: "small" as "small",
    },
  },
  MuiTable: {
    defaultProps: {
      size: "small" as "small",
    },
  },
  MuiToolbar: {
    defaultProps: {
      variant: "dense" as "dense",
    },
    styleOverrides: {
      dense: {
        minHeight: 44,
        "@media (min-width:600px)": {
          minHeight: 48,
        },
      },
    },
  },
});
